import { Alert, Button, ButtonBase, Card, CardContent, Checkbox, Chip, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ApiError, financeApi } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { StepChips } from "../../components/StepChips";
import { CurrencyCodeAutocomplete } from "../../components/CurrencyCodeAutocomplete";
import { AccountDraftPanel, accountTypeDefaults, CategoryDraftPanel, SetupEntityIcon } from "./GroupSetupForms";
import { groupSetupStorageKey, loadGroupSetupDraft, type GroupSetupDraft, type SelectableCategoryDraft } from "./groupSetupDraft";

const steps = ["Group", "Currencies", "Categories", "Accounts", "Review"];
type CategoryPanelState = { kind: "closed" } | { kind: "create"; initialType: "expense" | "income" } | { kind: "edit"; categoryId: string };
type AccountPanelState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; index: number };

function categoryBranches(categories: SelectableCategoryDraft[], type: "expense" | "income") {
  const typed = categories.filter((item) => item.type === type);
  return typed
    .filter((item) => !item.parent_client_id)
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((category) => ({
      category,
      children: typed.filter((item) => item.parent_client_id === category.client_id).sort((left, right) => left.name.localeCompare(right.name)),
    }));
}

export function GroupSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: queryKeys.me, queryFn: financeApi.me, retry: false });
  const groupsQuery = useQuery({ queryKey: queryKeys.groups, queryFn: financeApi.groups, retry: false });
  const defaultsQuery = useQuery({ queryKey: queryKeys.defaultGroupCategories, queryFn: financeApi.defaultGroupCategories, retry: false });
  const supportedCurrenciesQuery = useQuery({ queryKey: queryKeys.supportedCurrencies, queryFn: financeApi.supportedCurrencies, retry: false });
  const [draft, setDraft] = useState<GroupSetupDraft | null>(null);
  const [step, setStep] = useState(0);
  const [currencyInput, setCurrencyInput] = useState("");
  const [currencyError, setCurrencyError] = useState("");
  const [status, setStatus] = useState("");
  const [categoryPanel, setCategoryPanel] = useState<CategoryPanelState>({ kind: "closed" });
  const [accountPanel, setAccountPanel] = useState<AccountPanelState>({ kind: "closed" });

  useEffect(() => {
    if (meQuery.data && !draft) setDraft(loadGroupSetupDraft(meQuery.data));
  }, [draft, meQuery.data]);

  useEffect(() => {
    if (!draft || draft.categoriesInitialized || !defaultsQuery.data) return;
    setDraft({ ...draft, categoriesInitialized: true, categories: defaultsQuery.data.map((item) => ({ ...item, selected: true })) });
  }, [defaultsQuery.data, draft]);

  useEffect(() => {
    if (!draft || draft.accountsInitialized) return;
    setDraft({
      ...draft,
      accountsInitialized: true,
      accounts: [{ name: "Cash", type: "cash", icon: "cash", color: "#2563eb", currency: draft.baseCurrency, initial_balance: "0.00" }],
    });
  }, [draft]);

  useEffect(() => {
    if (draft && meQuery.data) localStorage.setItem(groupSetupStorageKey(meQuery.data.id), JSON.stringify(draft));
  }, [draft, meQuery.data]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  const selectedCategories = useMemo(() => draft?.categories.filter((item) => item.selected) || [], [draft?.categories]);
  const hasExistingGroups = Boolean(groupsQuery.data?.length);
  const createdCurrencies = useMemo(() => {
    if (!draft) return [];
    return Array.from(new Set([draft.baseCurrency, ...draft.additionalCurrencies, ...draft.accounts.map((account) => account.currency)])).filter(Boolean).sort();
  }, [draft]);

  const createMutation = useMutation({
    mutationFn: () => financeApi.createGroup({
      name: draft!.name.trim(),
      base_currency: draft!.baseCurrency,
      additional_currencies: draft!.additionalCurrencies,
      categories: selectedCategories.map(({ selected: _selected, is_custom: _custom, ...category }) => category),
      accounts: draft!.accounts,
    }),
    onSuccess: async (group) => {
      if (meQuery.data) localStorage.removeItem(groupSetupStorageKey(meQuery.data.id));
      localStorage.setItem("financeSelectedGroupId", group.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.groups });
      navigate("/home", { replace: true });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Could not create group"),
  });

  if (meQuery.error instanceof ApiError && meQuery.error.status === 401) return <Navigate to="/" replace />;
  if (meQuery.isLoading || !draft) return <section className="group-setup-shell"><p>Loading group setup...</p></section>;

  const update = (changes: Partial<GroupSetupDraft>) => setDraft((current) => current ? { ...current, ...changes } : current);
  const updateBaseCurrency = (value: string) => {
    const next = value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
    const old = draft.baseCurrency;
    update({
      baseCurrency: next,
      additionalCurrencies: draft.additionalCurrencies.filter((code) => code !== next),
      accounts: draft.accounts.map((account) => account.type === "cash" && account.name === "Cash" && account.currency === old ? { ...account, currency: next } : account),
    });
  };
  const addCurrency = () => {
    const code = currencyInput.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code)) return setCurrencyError("Currency must be a 3-letter ISO code.");
    if (code === draft.baseCurrency) return setCurrencyError(`${code} is already the base currency.`);
    if (draft.additionalCurrencies.includes(code)) return setCurrencyError(`${code} has already been added.`);
    update({ additionalCurrencies: [...draft.additionalCurrencies, code] });
    setCurrencyInput("");
    setCurrencyError("");
    setStatus("");
  };
  const toggleCategory = (categoryId: string, checked: boolean) => update({
    categories: draft.categories.map((item) => item.client_id === categoryId || (!checked && item.parent_client_id === categoryId) ? { ...item, selected: checked } : item),
  });
  const saveCategory = (category: SelectableCategoryDraft) => update({
    categories: draft.categories.some((item) => item.client_id === category.client_id)
      ? draft.categories.map((item) => item.client_id === category.client_id ? category : category.parent_client_id === item.client_id && category.selected ? { ...item, selected: true } : item)
      : [...draft.categories.map((item) => category.parent_client_id === item.client_id ? { ...item, selected: true } : item), category],
  });
  const deleteCategory = (categoryId: string) => update({ categories: draft.categories.filter((item) => item.client_id !== categoryId && item.parent_client_id !== categoryId) });
  const next = () => {
    setStatus("");
    setCurrencyError("");
    if (step === 0 && !draft.name.trim()) return setStatus("Group name is required.");
    if (step === 0 && !/^[A-Z]{3}$/.test(draft.baseCurrency)) return setStatus("Base currency must be a 3-letter ISO code.");
    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  return (
    <section className="group-setup-shell">
      <Card className="group-setup-card">
        <CardContent>
          <div className="group-setup-heading">
            <StepChips activeStep={step} ariaLabel="Group setup progress" className="group-setup-steps" steps={steps.map((label, index) => `${index + 1} ${label}`)} />
            <h1>Set up your group</h1>
            <p>Nothing is created until you confirm the final step. Progress will be available after reload</p>
          </div>

          <div className="group-setup-content">
            {step === 0 ? (
              <div className="group-setup-section">
                <h2>Name and base currency</h2>
                <p>Choose the reporting currency for the group. You can change it later only until the first record is created.</p>
                <TextField fullWidth required label="Group name" value={draft.name} onChange={(event) => update({ name: event.target.value })} />
                <CurrencyCodeAutocomplete
                  required
                  label="Base currency"
                  value={draft.baseCurrency}
                  currencies={supportedCurrenciesQuery.data || []}
                  helperText="Choose a configured currency or enter any 3-letter ISO code."
                  onChange={updateBaseCurrency}
                />
              </div>
            ) : null}

            {step === 1 ? (
              <div className="group-setup-section">
                <h2>Additional currencies</h2>
                <p>Add currencies you expect to use. Skipping keeps only {draft.baseCurrency}.</p>
                <div className="group-setup-add-row group-setup-currency-row">
                  <CurrencyCodeAutocomplete
                    label="Currency code"
                    value={currencyInput}
                    currencies={supportedCurrenciesQuery.data || []}
                    error={Boolean(currencyError)}
                    helperText={currencyError || "Choose a configured currency or enter any 3-letter ISO code."}
                    onChange={(code) => {
                      setCurrencyInput(code);
                      setCurrencyError("");
                    }}
                  />
                  <Button variant="outlined" onClick={addCurrency}>Add</Button>
                </div>
                <div className="group-setup-chips">
                  <Chip label={`${draft.baseCurrency} · Base`} color="primary" />
                  {draft.additionalCurrencies.map((code) => <Chip key={code} label={code} onDelete={() => update({ additionalCurrencies: draft.additionalCurrencies.filter((item) => item !== code) })} />)}
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="group-setup-section group-setup-category-section">
                <h2>Categories</h2>
                <p>Select the suggested categories you want to keep. If you skip this step, no categories will be created.</p>
                <div className="group-setup-category-columns">
                  {(["expense", "income"] as const).map((type) => (
                    <Card className={`group-setup-category-panel group-setup-category-${type}`} variant="outlined" key={type}>
                      <CardContent>
                        <div className="group-setup-list-heading">
                          <div><h3>{type === "expense" ? "Expense" : "Income"}</h3><span>{draft.categories.filter((item) => item.type === type && item.selected).length} selected</span></div>
                          <Button variant="outlined" size="small" onClick={() => setCategoryPanel({ kind: "create", initialType: type })}>Add {type}</Button>
                        </div>
                        <div className="group-setup-category-tree">
                          {categoryBranches(draft.categories, type).map(({ category, children }) => (
                            <div className="group-setup-category-branch" key={category.client_id}>
                              <div className="group-setup-draft-row">
                                <Checkbox checked={category.selected} onChange={(event) => toggleCategory(category.client_id, event.target.checked)} slotProps={{ input: { "aria-label": `Include ${category.name}` } }} />
                                <ButtonBase onClick={() => setCategoryPanel({ kind: "edit", categoryId: category.client_id })}><SetupEntityIcon icon={category.icon} color={category.color} /><span>{category.name}</span></ButtonBase>
                              </div>
                              {children.map((child) => <div className="group-setup-draft-row child" key={child.client_id}><Checkbox checked={child.selected} disabled={!category.selected} onChange={(event) => toggleCategory(child.client_id, event.target.checked)} slotProps={{ input: { "aria-label": `Include ${child.name}` } }} /><ButtonBase onClick={() => setCategoryPanel({ kind: "edit", categoryId: child.client_id })}><SetupEntityIcon icon={child.icon} color={child.color} /><span>{child.name}</span></ButtonBase></div>)}
                            </div>
                          ))}
                          {!categoryBranches(draft.categories, type).length ? <p className="status">No {type} categories.</p> : null}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="group-setup-section">
                <h2>Accounts</h2>
                <p>Cash is suggested by default. Remove it or skip this step to create the group without accounts.</p>
                <div className="group-setup-list-heading"><div><h3>Accounts</h3><span>{draft.accounts.length} configured</span></div><Button variant="contained" onClick={() => setAccountPanel({ kind: "create" })}>Add account</Button></div>
                <div className="group-setup-entities">
                  {draft.accounts.map((account, index) => <ButtonBase className={`group-setup-account-row account-${account.type}`} key={`${account.name}-${index}`} onClick={() => setAccountPanel({ kind: "edit", index })}><SetupEntityIcon icon={account.icon} color={account.color} /><span><strong>{account.name}</strong><small>{accountTypeDefaults[account.type]?.label || account.type} · {account.currency} · {account.initial_balance}</small></span></ButtonBase>)}
                  {!draft.accounts.length ? <p className="status">No accounts will be created.</p> : null}
                </div>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="group-setup-section">
                <h2>Review</h2>
                <p>Review the entities that will be created together.</p>
                <dl className="group-setup-review"><div><dt>Group</dt><dd>{draft.name}</dd></div><div><dt>Base currency</dt><dd>{draft.baseCurrency}</dd></div><div><dt>Currencies</dt><dd>{createdCurrencies.length ? createdCurrencies.join(", ") : "None"}</dd></div><div><dt>Categories</dt><dd>{selectedCategories.length}</dd></div><div><dt>Accounts</dt><dd>{draft.accounts.length}</dd></div></dl>
              </div>
            ) : null}
          </div>

          {status ? <Alert severity="error">{status}</Alert> : null}
          <div className="group-setup-actions">
            {step > 0 || hasExistingGroups ? (
              <Button
                variant="outlined"
                disabled={createMutation.isPending}
                onClick={() => {
                  setStatus("");
                  setCurrencyError("");
                  if (step === 0) navigate("/settings");
                  else setStep((current) => current - 1);
                }}
              >
                Back
              </Button>
            ) : null}
            <div>
              {step > 0 && step < 4 ? <Button type="button" disabled={createMutation.isPending} onClick={() => { if (step === 1) update({ additionalCurrencies: [] }); if (step === 2) update({ categories: draft.categories.map((item) => ({ ...item, selected: false })) }); if (step === 3) update({ accounts: [] }); next(); }}>Skip</Button> : null}
              {step < 4 ? <Button variant="contained" onClick={next}>Continue</Button> : <Button variant="contained" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>{createMutation.isPending ? "Creating..." : "Create group"}</Button>}
            </div>
          </div>
        </CardContent>
      </Card>
      {categoryPanel.kind !== "closed" ? <CategoryDraftPanel key={categoryPanel.kind === "edit" ? categoryPanel.categoryId : `create-${categoryPanel.initialType}`} categories={draft.categories} category={categoryPanel.kind === "edit" ? draft.categories.find((item) => item.client_id === categoryPanel.categoryId) : undefined} initialType={categoryPanel.kind === "create" ? categoryPanel.initialType : undefined} onClose={() => setCategoryPanel({ kind: "closed" })} onDelete={deleteCategory} onSave={saveCategory} /> : null}
      {accountPanel.kind !== "closed" ? <AccountDraftPanel key={accountPanel.kind === "edit" ? `edit-${accountPanel.index}` : "create"} account={accountPanel.kind === "edit" ? draft.accounts[accountPanel.index] : undefined} currencies={Array.from(new Set([draft.baseCurrency, ...draft.additionalCurrencies]))} onClose={() => setAccountPanel({ kind: "closed" })} onDelete={() => { if (accountPanel.kind === "edit") update({ accounts: draft.accounts.filter((_, index) => index !== accountPanel.index) }); }} onSave={(account) => update({ accounts: accountPanel.kind === "edit" ? draft.accounts.map((item, index) => index === accountPanel.index ? account : item) : [...draft.accounts, account] })} /> : null}
    </section>
  );
}
